import { Injectable } from "@angular/core";
import { FormArray, FormControl, FormGroup, Validators } from "@angular/forms";
import { connectApi } from "./connect-api";
import { Observable, of } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { map } from "rxjs/internal/operators";
import { Utils } from "./utils";
import { Contact } from "./contact";
import { StaffProfiles } from "./model/staff-profiles";
import { SESSION_STORAGE_KEYS } from "./session-storage-keys";
import { User } from "./user";
import { CustomHttpParams } from "./connect-http-interceptor";
import { ReferralForm } from "../referral/referral-form";
import { Telephone } from "../shared/telephone";
import { TelephoneTypes } from "../shared/telephone-types";
import moment from "moment";
import { Identification } from "../shared/identification";
import {
  CirfFieldStatusType,
  CirfFieldStatusValue,
} from "../cirf/config/cirf-field";
import { CirfConfigService } from "../cirf/config/cirf-config-service";
import { CIRF_CONFIG_KEYS } from "../cirf/config/cirf-config-keys";
import { CirfUdf } from "../cirf/cirf-udf";
import { CirfMortgage } from "../cirf/cirf-mortgage";

@Injectable({
  providedIn: "root",
})
export class CommonService {
  private cachedLoggedInUser: StaffProfiles;

  constructor(
    private httpClient: HttpClient,
    private cirfConfigService: CirfConfigService
  ) {}

  createFormControls(
    object: any,
    allowMissingFields?: boolean,
    parentEntityName?: string,
    index?: number
  ): FormGroup {
    let group = {};
    if (object) {
      if (object["allowMissingFields"]) {
        allowMissingFields = true;
      }

      for (let prop in object) {
        if (prop == "birthDate") {
          // initialize the birthDate field coming from Unity
          group[prop] = new FormControl(object[prop]);
        } else if (object.hasOwnProperty(prop)) {
          if (object[prop] instanceof Array) {
            if (object[prop].every((obj) => this.isTypeUDF(obj))) {
              this.buildUdfFromConfig(object, object[prop], index);
            }
            group[prop] = new FormArray(
              object[prop].map((objectArray, idx) => {
                if (object.entityName == "broker") {
                  if (parentEntityName == "fireInsuranceDetail") {
                    return this.createFormControls(
                      objectArray,
                      allowMissingFields,
                      "fireInsuranceBroker",
                      idx + 1
                    );
                  } else {
                    return this.createFormControls(
                      objectArray,
                      allowMissingFields,
                      parentEntityName + "Broker",
                      idx + 1
                    );
                  }
                } else {
                  return this.createFormControls(
                    objectArray,
                    allowMissingFields,
                    object.entityName,
                    idx + 1
                  );
                }
              })
            );
          } else if (object[prop] instanceof Object) {
            let parentName = object.entityName;
            if (object instanceof CirfMortgage) {
              parentName = object.isExistingMortgage()
                ? "existingMortgage"
                : "newMortgage";
            }
            group[prop] = this.createFormControls(
              object[prop],
              allowMissingFields,
              parentName
            );
          } else if (prop && prop.toLocaleLowerCase().indexOf("email") > -1) {
            if (
              this.isRequiredField(object, prop, parentEntityName, index) &&
              !object["allowMissingFields"] &&
              !allowMissingFields
            ) {
              group[prop] = new FormControl(object[prop], [
                Validators.required,
                Validators.email,
              ]);
            } else {
              group[prop] = new FormControl(object[prop], Validators.email);
            }
          } else if (
            this.isRequiredField(object, prop, parentEntityName, index) &&
            !object["allowMissingFields"] &&
            !allowMissingFields
          ) {
            group[prop] = new FormControl(object[prop], Validators.required);
          } else {
            if (prop == "expiryDate" && object instanceof Identification) {
              object[prop] = this.convertPartialDateToDisplay(object[prop]);
            }
            group[prop] = new FormControl(object[prop]);
          }
        }
      }
    }
    return new FormGroup(group);
  }

  /**
   * Converts Partial Date from "YYYY/MM/DD" to "DD/MM/YYYY"
   *
   * @param {string} partialDate in the "YYYY/MM/DD", "YYYY//" , "/MM/DD", "//DD" ... format
   * @return {string} date format "DD/MM/YYYY" or partial: "DD", "DD/MM", "MM/YYYY" or "YYYY"
   */
  convertPartialDateToDisplay(partialDate: string): string {
    if (partialDate) {
      if (partialDate.length > 10) {
        //That means it's a full date (To accomodate full date in production)
        let date = moment(partialDate);
        if (date.isValid()) {
          return date.format("DD/MM/YYYY");
        } else {
          return "";
        }
      } else if (partialDate.length == 10) {
        //That means it's a full date
        let date;
        if (partialDate[2] == "/") {
          //We have to specify the format of the date So 10/11/2022 May be converted to Oct 11 instead of Nov 10
          date = moment(partialDate, "DD/MM/YYYY");
        } else {
          date = moment(partialDate, "YYYY/MM/DD");
        }

        if (date.isValid()) {
          return date.format("DD/MM/YYYY");
        } else {
          return "";
        }
      } else {
        //It's a partial Date
        let dateList = partialDate.split("/");
        let returnValue = dateList.filter((value) => !!value);
        if (returnValue) {
          if (dateList.length == 3) {
            if (!dateList[0].length) {
              // Format YYYY/MM/DD OR /MM/DD
              return returnValue.reverse().join("/");
            } else {
              //Format DD/MM/YYYY
              return returnValue.join("/");
            }
          } else if (dateList.length == 1 || dateList.length == 2) {
            return returnValue.join("/");
          } else {
            return "";
          }
        } else {
          return "";
        }
      }
    } else {
      return "";
    }
  }

  /**
   * Converts Partial Date from "DD/MM/YYYY" to "YYYY/MM/DD"
   *
   * @param {string} partialDateValue in "DD/MM/YYYY" or partial: "DD", "DD/MM", "MM/YYYY" or "YYYY"
   * @return {string} date in "YYYY/MM/DD", "YYYY//" , "/MM/DD", "//DD" ... format
   */
  convertPartialDateToSave(partialDate: string): string {
    if (partialDate) {
      if (partialDate.length > 10) {
        //That means it's a full date (To accommodate full date in production)
        let date = moment(partialDate);
        if (date.isValid()) {
          return date.format("YYYY/MM/DD");
        } else {
          return "";
        }
      } else if (partialDate.length >= 8 && partialDate.length <= 10) {
        //That means it's a full date 1/1/2000 or 01/01/2000
        let date = moment(partialDate, "DD/MM/YYYY");
        if (date.isValid()) {
          return date.format("YYYY/MM/DD");
        } else {
          return "";
        }
      } else {
        //It's a partial Date
        let dateList = partialDate.split("/");
        if (dateList.length == 1) {
          if (dateList[0].length == 1 || dateList[0].length == 2) {
            return "//" + dateList[0]; /* D OR DD - example 14 */
          } else if (dateList[0].length == 4) {
            return dateList[0] + "//"; /* YYYY// - example 2020// */
          } else {
            return "";
          }
        }
        if (dateList.length == 2) {
          if (
            (dateList[0].length == 1 || dateList[0].length == 2) &&
            (dateList[1].length == 1 || dateList[1].length == 2)
          ) {
            /*  "DD/MM" */
            return (
              "/" + dateList[1] + "/" + dateList[0]
            ); /* '/6/4'  OR  '/06/14'   */
          } else if (
            (dateList[0].length == 1 || dateList[0].length == 2) &&
            dateList[1].length == 4
          ) {
            /* "M/YYYY" OR  "MM/YYYY" */
            return (
              dateList[1] + "/" + dateList[0] + "/"
            ); /*   '2020/6' OR '2020/06'   */
          } else {
            return "";
          }
        }
      }
    } else {
      return "";
    }
  }

  // Method is used to convert your reactive form object to model object
  // it overwrite data which matches in form object onto model object
  // form is your form object , object is model which you want to update
  // and we require type in case we need to create new object on already existing model object.
  updateFormObject(form, type, object) {
    return this.readFormGroupObject(form, type, object);
  }

  // Method is used to convert your reactive form object to new model object
  readFormObject(form, type) {
    return this.readFormGroupObject(form, type, undefined);
  }

  readFormGroupObject(form, type, object) {
    const metadata = this.getMetadata(type);
    const modelInstance = !!object ? object : new type();
    const fieldKeys = Object.keys(metadata.properties);
    for (const fieldKey of fieldKeys) {
      const fieldMetadata = metadata.properties[fieldKey];
      modelInstance[fieldKey] = this.readFormFieldObject(
        fieldMetadata,
        form.get(fieldKey),
        modelInstance[fieldKey]
      );
    }
    return modelInstance;
  }

  getMetadata(target) {
    const defaultModelDescriptor = {
      asyncValidators: [],
      validators: [],
      properties: {},
    };
    const reflectedMetadata = Reflect.getMetadata(
      "rx-form-mapper-metadata",
      target
    );
    return Object.assign({}, defaultModelDescriptor, reflectedMetadata);
  }

  readFormFieldObject(fieldMetadata, abstractControl, object) {
    let fieldValue;
    if (abstractControl == null) {
      fieldValue = void 0;
    } else if (fieldMetadata.type === "FORM_CONTROL") {
      fieldValue = abstractControl.value;
    } else if (fieldMetadata.type === "FORM_GROUP") {
      fieldValue = this.readFormGroupObject(
        abstractControl,
        fieldMetadata.propertyType,
        object
      );
    } else if (fieldMetadata.type === "FORM_ARRAY") {
      fieldValue = abstractControl.controls.map((fg, index) =>
        this.readFormGroupObject(
          fg,
          fieldMetadata.propertyGenericArgumentType,
          object[index]
        )
      );
    } else if (fieldMetadata.type === "CUSTOM") {
      const customMapper = new fieldMetadata.customMapper();
      fieldValue = customMapper.readForm(
        abstractControl,
        fieldMetadata.propertyType
      );
    }
    return fieldValue;
  }

  getSolicitorsForLawFirm(legalFirmId: number) {
    let url: string;
    url = `${
      connectApi.contacts
    }/${legalFirmId}/solicitors?sort=contactName.lastName|ASC&filter=contactType_EQ_SOLICITOR|activeFlag_IN_${Utils.escapeSearchText(
      "Y_n"
    )}!YES`;
    return this.httpClient
      .get<any[]>(Utils.encodePipe(Utils.normalizeUrl(url)))
      .pipe(
        map((res) => {
          let contactsList: Contact[] = res["Solicitors"];
          return contactsList
            ? contactsList.map((item) => {
                return new Contact(item);
              })
            : [];
        })
      );
  }

  get cachedLoggedInStaffProfile(): StaffProfiles {
    return this.cachedLoggedInUser;
  }

  getCachedLoggedInStaffProfile(): Observable<StaffProfiles> {
    let userId: string;
    let user = sessionStorage.getItem(SESSION_STORAGE_KEYS.user);
    if (user) {
      let sessionUser: User = new User(JSON.parse(user));
      userId = sessionUser.username;
    }

    if (
      !!userId &&
      (!this.cachedLoggedInUser || this.cachedLoggedInUser.userId != userId)
    ) {
      return this.httpClient
        .get<StaffProfiles>(connectApi.loggedInStaffProfiles)
        .pipe(
          map((res) => {
            this.cachedLoggedInUser = new StaffProfiles(res["StaffProfile"]);
            return new StaffProfiles(res["StaffProfile"]);
          })
        );
    } else {
      return of(new StaffProfiles(this.cachedLoggedInUser));
    }
  }

  updatedLoggedInStaffProfile(referralForm: ReferralForm): void {
    if (this.isStaffProfileUpdated(referralForm)) {
      // console.log('updatedLoggedInStaffProfile');
      // console.table(this.cachedLoggedInUser.user.userRegistrationStatus);
      this.httpClient
        .put<StaffProfiles>(
          connectApi.loggedInStaffProfiles,
          this.cachedLoggedInUser,
          {
            params: new CustomHttpParams(true),
          }
        )
        .subscribe((res) => {
          this.cachedLoggedInUser = new StaffProfiles(res["User"]);
        });
    }
  }

  invalidateCachedLoggedInUser() {
    this.cachedLoggedInUser = null;
  }

  isStaffProfileUpdated(referralForm: ReferralForm): boolean {
    let isProfileUpdated: boolean = false;
    if (this.cachedLoggedInUser && referralForm) {
      if (this.cachedLoggedInUser.contact) {
        if (this.cachedLoggedInUser.contact.contactName) {
          if (
            Utils.isNotNullorUndefined(referralForm.referringAgentFirstName) &&
            referralForm.referringAgentFirstName.toString() != "" &&
            referralForm.referringAgentFirstName !==
              this.cachedLoggedInUser.contact.contactName.firstName
          ) {
            this.cachedLoggedInUser.contact.contactName.firstName =
              referralForm.referringAgentFirstName;
            isProfileUpdated = true;
          }
          if (
            Utils.isNotNullorUndefined(referralForm.referringAgentMiddleName) &&
            referralForm.referringAgentLastName.toString() != "" &&
            referralForm.referringAgentMiddleName !==
              this.cachedLoggedInUser.contact.contactName.middleName
          ) {
            this.cachedLoggedInUser.contact.contactName.middleName =
              referralForm.referringAgentMiddleName;
            isProfileUpdated = true;
          }
          if (
            Utils.isNotNullorUndefined(referralForm.referringAgentLastName) &&
            referralForm.referringAgentLastName.toString() != "" &&
            referralForm.referringAgentLastName !==
              this.cachedLoggedInUser.contact.contactName.lastName
          ) {
            this.cachedLoggedInUser.contact.contactName.lastName =
              referralForm.referringAgentLastName;
            isProfileUpdated = true;
          }
        }
        if (
          Utils.isNotNullorUndefined(referralForm.referringAgentCompany) &&
          referralForm.referringAgentCompany.toString() != "" &&
          referralForm.referringAgentCompany !==
            this.cachedLoggedInUser.contact.companyName
        ) {
          this.cachedLoggedInUser.contact.companyName =
            referralForm.referringAgentCompany;
          isProfileUpdated = true;
        }
        if (
          Utils.isNotNullorUndefined(referralForm.referringAgentEmail) &&
          referralForm.referringAgentEmail.toString() != "" &&
          referralForm.referringAgentEmail !==
            this.cachedLoggedInUser.contact.email
        ) {
          this.cachedLoggedInUser.contact.email =
            referralForm.referringAgentEmail;
          isProfileUpdated = true;
        }
        if (
          referralForm.referringAgentTelephones &&
          referralForm.referringAgentTelephones.length > 0 &&
          !!referralForm.referringAgentTelephones[0].telephoneNumber
        ) {
          if (!this.cachedLoggedInUser.contact.telephone) {
            this.cachedLoggedInUser.contact.telephone = [];
          }
          let phone: Telephone = this.cachedLoggedInUser.contact.telephone.find(
            (t: Telephone) => t.phoneTypeCode === TelephoneTypes.work
          );
          if (!phone) {
            phone = new Telephone();
            phone.phoneTypeCode = TelephoneTypes.work;
          }
          phone.telephoneNumber =
            referralForm.referringAgentTelephones[0].telephoneNumber;
          isProfileUpdated = true;
          this.cachedLoggedInUser.contact.telephone.push(phone);
        }
        if (
          Utils.isNotNullorUndefined(referralForm.referringAgentRole) &&
          referralForm.referringAgentRole !==
            this.cachedLoggedInUser.contact.contactType
        ) {
          this.cachedLoggedInUser.contact.contactType =
            referralForm.referringAgentRole;
          isProfileUpdated = true;
        }
      }
    }
    return isProfileUpdated;
  }

  isTypeUDF = (object) => {
    return object instanceof CirfUdf;
  };

  /**
   * Build the User defind fields and add it to the object array
   * @param parentObject - Parent Object , for example "CirfClientInfo" , "CirfMatterContactInfo", "CirfMortgage"
   * @param objectArray - The UDF array , for example "CirfClientInfoUdfs" , "matterContactInfoUdfs"
   * @param parentIndex - the index of the parent if the parent object is an array
   * @return void
   *
   */
  buildUdfFromConfig(
    parentObject: any,
    objectArray: any,
    parentIndex?: number
  ): void {
    if (!objectArray) {
      objectArray = [];
    }
    // Get the UDF config key
    let configName = parentObject.entityName;
    if (parentObject.entityName == "mortgage") {
      configName = parentObject.isExistingMortgage()
        ? "existingMortgage"
        : "newMortgage";
    }
    if (
      parentIndex &&
      parentObject.entityName != "cirfClientInfo" &&
      parentObject.entityName != "mortgage"
    ) {
      configName += parentIndex; // For example "identification1" & "identification2"
    }
    configName += "Udf";
    let udfKey = CIRF_CONFIG_KEYS[configName];
    //console.log(configName, udfKey);

    if (udfKey) {
      //Get all matching keys
      let udfCount = this.cirfConfigService.getSectionUdfCount(udfKey);
      for (let i = 0; i < udfCount; i++) {
        let cirfUDF: CirfUdf;
        if (objectArray.length >= udfCount) {
          cirfUDF = objectArray[i];
        } else {
          cirfUDF = new CirfUdf();
          cirfUDF.fieldValue = "";
          objectArray.push(cirfUDF);
        }
        cirfUDF.fieldLabel = this.cirfConfigService.getUdfFieldLabel(
          udfKey + i
        );
        cirfUDF.parentName = parentObject.entityName;
        if (parentObject.entityName == "identification") {
          cirfUDF.parentName = parentObject.entityName + parentIndex;
        }
        if (parentObject.entityName == "mortgage") {
          cirfUDF.parentName = parentObject.isExistingMortgage()
            ? "existingMortgage"
            : "newMortgage";
        }
        let fieldConfigKey =
          CIRF_CONFIG_KEYS[cirfUDF.parentName + "Udf"] + String(i);
        //console.log(fieldConfigKey);
      }
    }
  }

  /**
   * Check if the field is a required field
   * @param obj - object that includes the field, For example "CirfClientInfo"
   * @param propertyKey - the name of the field
   * @param parentEntityName - the name of the parent entity
   * @param index - index of the field in case of an array
   * @return boolean : true if the field is required
   *
   */
  isRequiredField(
    obj: any,
    propertyKey: string,
    parentEntityName?: string,
    index?: number
  ): boolean {
    let fieldConfigKey = "";
    if (obj instanceof CirfUdf && parentEntityName && index) {
      let fieldConfigName = parentEntityName;
      if (obj.parentName) {
        fieldConfigName = obj.parentName;
      }
      fieldConfigName += "Udf";
      fieldConfigKey = CIRF_CONFIG_KEYS[fieldConfigName] + String(index - 1);
      //console.log(fieldConfigKey);
    } else {
      fieldConfigKey = this.getFieldConfigKey(
        obj,
        propertyKey,
        parentEntityName,
        index
      );
    }
    let value = fieldConfigKey
      ? this.cirfConfigService.isFieldMandatory(fieldConfigKey)
      : false;
    //console.log(fieldConfigKey, value);
    return value;
  }

  isFieldMandatory(fieldName: string): boolean {
    return this.cirfConfigService.isFieldByNameMandatory(fieldName);
  }

  /**
   * Get the key from the CIRF_CONFIG_KEYS
   * @param obj - object that includes the field, For example "CirfClientInfo"
   * @param propertyKey - the name of the field
   * @param parentEntityName - the name of the parent entity
   * @param index - index of the field in case of an array
   * @return string : the value of the key if found
   *
   */
  getFieldConfigKey(
    obj: any,
    propertyKey: string,
    parentEntityName?: string,
    index?: number
  ): string {
    let value = "";
    let key = CIRF_CONFIG_KEYS;
    if (parentEntityName) {
      key = this.updateKey(key, parentEntityName);
    }
    if (obj.entityName) {
      key = this.updateKey(key, obj.entityName);
    }
    if (index && obj.entityName != "cirfClientInfo") {
      if (obj.entityName == "mortgage") {
        let label = obj.isExistingMortgage()
          ? "existingMortgage"
          : "newMortgage";
        key = this.updateKey(key, label);
      } else {
        key = this.updateKey(key, obj.entityName + index);
      }
    }
    if (key[propertyKey]) {
      value = key[propertyKey];
    }
    return value;
  }

  updateKey(obj: any, key: string): any {
    if (obj[key]) {
      obj = obj[key];
    }
    return obj;
  }
}

export const metadataKey = "Required";

export function isValidatorRequired<T>(instance: T, propertyKey: string) {
  return !!Reflect.getMetadata(metadataKey, instance, propertyKey);
}
